import { Plugin, type ViteDevServer } from 'vite';
import type { IncomingMessage, ServerResponse } from 'http';
import { KulpBridgeConfig } from './types';
import {
  sendErrorToParent,
  parseViteErrorHtml,
  getErrorReporterScript
} from './bridges/error-reporter';
import { getRouteTrackerScript } from './bridges/route-tracker';
import { getBadgeScript } from './bridges/badge';
import { createComponentTaggerPlugin } from './bridges/component-tagger';
import { getConsoleLoggerScript } from './bridges/console-logger';

// Default configuration
const defaultConfig: KulpBridgeConfig = {
  enableErrorReporter: true,
  enableRouteTracker: true,
  enableBadge: true,
  enableComponentTagger: true,
  componentTaggerConfig: {
    enableTailwindGeneration: false,
    tailwindConfigPath: './tailwind.config.ts',
    tailwindOutputPath: './src/tailwind.config.kulp.json'
  },
  enableConsoleLogger: true,
  enablePerformanceTracker: false,
};

export function viteBridge(config: KulpBridgeConfig = {}): Plugin[] {
  const finalConfig = { ...defaultConfig, ...config };

  const plugins: Plugin[] = [];

  // Add component tagger plugin if enabled
  if (finalConfig.enableComponentTagger) {
    plugins.push(createComponentTaggerPlugin(finalConfig.componentTaggerConfig));
  }

  // Add main bridge plugin
  plugins.push({
    name: 'kulp-bridge',

    configureServer(server: ViteDevServer) {
      if (!finalConfig.enableErrorReporter) return;

      // Handle HMR errors
      server.ws.on('error', (error: unknown) => {
        sendErrorToParent(error);
      });

      // Handle server errors by intercepting error responses
      server.middlewares.use((req: IncomingMessage, res: ServerResponse, next: () => void) => {
        const originalEnd = res.end;
        // Use broad parameter types for compatibility with Node's overloads
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (res as any).end = function (chunk?: unknown, encoding?: unknown, callback?: unknown) {
          if (res.statusCode >= 400 && typeof chunk === 'string') {
            const errorData = parseViteErrorHtml(chunk);
            if (errorData) {
              sendErrorToParent(errorData);
            }
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return (originalEnd as any).call(this, chunk as any, encoding as any, callback as any);
        };
        next();
      });
    },

    buildStart() {
      // Initialize bridges when build starts
      if (finalConfig.enableErrorReporter) {
        console.log('[KulpBridge] Error reporter initialized');
      }
      if (finalConfig.enableRouteTracker) {
        console.log('[KulpBridge] Route tracker initialized');
      }
      if (finalConfig.enableBadge) {
        console.log('[KulpBridge] Badge initialized');
      }
      if (finalConfig.enableComponentTagger) {
        console.log('[KulpBridge] Component tagger initialized');
      }
    },

  buildEnd(error: unknown) {
      if (error && finalConfig.enableErrorReporter) {
        sendErrorToParent(error);
      }
    },

  renderError(error: unknown) {
      if (finalConfig.enableErrorReporter) {
        sendErrorToParent(error);
      }
      // Don't prevent default error handling
    },

    // Transform hook to inject bridge scripts in development
    transformIndexHtml: {
      order: 'pre',
      handler(html: string) {
        const isDevelopment = process.env.NODE_ENV === 'development';
        if (isDevelopment) {
          let scriptsToInject = '';

          // Add error reporter script
          if (finalConfig.enableErrorReporter) {
            scriptsToInject += getErrorReporterScript();
          }

          // Add route tracker script
          if (finalConfig.enableRouteTracker) {
            scriptsToInject += getRouteTrackerScript();
          }

          // Add badge script
          if (finalConfig.enableBadge) {
            scriptsToInject += getBadgeScript();
          }

          // Future bridges can be added here
          if (finalConfig.enableConsoleLogger) {
            scriptsToInject += getConsoleLoggerScript();
          }

          if (scriptsToInject) {
            return html.replace('<head>', `<head>${scriptsToInject}`);
          }
        }
        return html;
      }
    }
  });

  return plugins;
}

// Export types for consumers
export type {
  BuildError,
  KulpBridgeConfig,
  ComponentTaggerConfig,
  KulpMessage,
  RouteChangeMessage,
  CurrentRouteMessage,
  RequestRouteMessage,
  ErrorMessage
  , RequestConsoleLogsMessage, StopConsoleLogsMessage
} from './types';

// Export bridge functions for advanced usage
export {
  sendErrorToParent,
  parseViteErrorHtml,
  getErrorReporterScript,
  getRouteTrackerScript,
  getBadgeScript,
  createComponentTaggerPlugin,
  shouldTagElement,
  findProjectRoot
} from './bridges';

// Default export for convenience
export default viteBridge; 